import { useQuery, useQueries, useQueryClient } from 'react-query';
import { Options } from '@frontend/fetch';
import { LimitedSchemaFetchQueryOptions, LimitedSchemaQueryOptions } from '@frontend/react-query-helpers';
import { SchemaSMSDataV4Service } from '../service';
import { BatchGetGroupThreadStatusIO } from '../types';

export const getBatchGetGroupThreadStatusQueryKey = (req: BatchGetGroupThreadStatusIO['input']) =>
  ['V4', 'BatchGetGroupThreadStatus', req] as const;

type QueryKey = ReturnType<typeof getBatchGetGroupThreadStatusQueryKey>;

/**
 * A hook that returns a query for the `BatchGetGroupThreadStatus` endpoint.
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useBatchGetGroupThreadStatusQuery = <E = unknown, D = BatchGetGroupThreadStatusIO['output']>(
  req: BatchGetGroupThreadStatusIO['input'],
  options?: LimitedSchemaQueryOptions<BatchGetGroupThreadStatusIO, E, D, QueryKey>,
  httpOptions?: Options
) =>
  useQuery<BatchGetGroupThreadStatusIO['output'], E, D, QueryKey>({
    queryKey: getBatchGetGroupThreadStatusQueryKey(req),
    queryFn: () => SchemaSMSDataV4Service.BatchGetGroupThreadStatus(req, httpOptions),
    ...options,
  });

/**
 * A hook that returns an array of queries for the `BatchGetGroupThreadStatus` endpoint.
 * It takes an array of objects with the following properties:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useBatchGetGroupThreadStatusQueries = <E = unknown, D = BatchGetGroupThreadStatusIO['output']>(
  argsArr: {
    req: BatchGetGroupThreadStatusIO['input'];
    options?: LimitedSchemaQueryOptions<BatchGetGroupThreadStatusIO, E, D, QueryKey>;
    httpOptions?: Options;
  }[]
) =>
  useQueries(
    argsArr.map(({ req, options, httpOptions }) => ({
      queryKey: getBatchGetGroupThreadStatusQueryKey(req),
      queryFn: () => SchemaSMSDataV4Service.BatchGetGroupThreadStatus(req, httpOptions),
      ...options,
    }))
  ) as ReturnType<typeof useBatchGetGroupThreadStatusQuery<E, D>>[];

/**
 * A hook that returns a function to fetch the query for the `BatchGetGroupThreadStatus` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useFetchBatchGetGroupThreadStatus = () => {
  const queryClient = useQueryClient();

  const fetchBatchGetGroupThreadStatus = async <E = unknown, D = BatchGetGroupThreadStatusIO['output']>(
    req: BatchGetGroupThreadStatusIO['input'],
    options?: LimitedSchemaFetchQueryOptions<BatchGetGroupThreadStatusIO, E, D, QueryKey>,
    httpOptions?: Options
  ) => {
    const response = await queryClient.fetchQuery<BatchGetGroupThreadStatusIO['output'], E, D, QueryKey>(
      getBatchGetGroupThreadStatusQueryKey(req),
      () => SchemaSMSDataV4Service.BatchGetGroupThreadStatus(req, httpOptions),
      options
    );

    return response;
  };

  return fetchBatchGetGroupThreadStatus;
};
